import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtINR, fmtDate, downloadBlob } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileSpreadsheet } from "lucide-react";

export default function Ledger() {
  const { user } = useAuth();
  const { cid: paramCid } = useParams();
  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState(user?.role === "customer" ? user.customer_id : (paramCid || ""));
  const [entries, setEntries] = useState([]);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") api.get("/customers").then(r => {
      setCustomers(r.data);
      if (!cid && r.data.length) setCid(r.data[0].id);
    });
  }, [user]);

  useEffect(() => {
    if (!cid) return;
    api.get("/ledger", { params: { customer_id: cid } }).then(r => setEntries(r.data));
    api.get("/customers").then(r => setCustomer(r.data.find(c => c.id === cid)));
  }, [cid]);

  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedEntries = [...entries].sort((a, b) => {
    if (sortField === "date") {
      const dDiff = new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime();
      if (dDiff !== 0) return sortDir === "asc" ? dDiff : -dDiff;
      const cDiff = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
      return sortDir === "asc" ? cDiff : -cDiff;
    }
    let valA, valB;
    switch (sortField) {
      case "type":
        valA = a.transaction_type || "";
        valB = b.transaction_type || "";
        break;
      case "ref":
        valA = a.reference_no || "";
        valB = b.reference_no || "";
        break;
      case "desc":
        valA = a.remarks || "";
        valB = b.remarks || "";
        break;
      case "debit":
        valA = a.debit || 0;
        valB = b.debit || 0;
        break;
      case "credit":
        valA = a.credit || 0;
        valB = b.credit || 0;
        break;
      case "balance":
        valA = -a.balance;
        valB = -b.balance;
        break;
      default:
        valA = 0;
        valB = 0;
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });


  const renderSortIndicator = (field) => {
    if (sortField !== field) return <span className="text-slate-300 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">↕</span>;
    return <span className="text-cobalt-600 font-bold ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div data-testid="ledger-page">
      <PageHeader
        eyebrow="Statement"
        title={user?.role === "customer" ? "My Ledger" : "Customer Ledger"}
        description="Running balance of all debit and credit transactions. Advance auto-adjusts."
        actions={cid && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={()=>downloadBlob(`/export/ledger.xlsx?customer_id=${cid}`, "ledger.xlsx")} data-testid="ledger-export-xlsx"><FileSpreadsheet size={14} className="mr-1"/>Excel</Button>
            <Button onClick={()=>downloadBlob(`/export/statement/${cid}.pdf`, "statement.pdf")} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="ledger-export-pdf"><FileDown size={14} className="mr-1"/>Statement PDF</Button>
          </div>
        )}
      />

      {user?.role === "admin" && (
        <Card padding="p-4" className="mb-4">
          <div className="max-w-md">
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Customer</label>
            <Select value={cid} onValueChange={setCid}>
              <SelectTrigger className="mt-1" data-testid="ledger-customer-select"><SelectValue placeholder="Choose customer"/></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </Card>
      )}

      {customer && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card padding="p-4"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Customer</div><div className="font-heading font-semibold text-lg mt-1">{customer.business_name}</div><div className="text-xs text-slate-500">{customer.mobile}</div></Card>
          <Card padding="p-4"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Outstanding</div><div className="font-mono font-bold text-2xl mt-1 text-rose-600">{fmtINR(customer.outstanding||0)}</div></Card>
          <Card padding="p-4"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Advance</div><div className="font-mono font-bold text-2xl mt-1 text-emerald-600">{fmtINR(customer.advance||0)}</div></Card>
        </div>
      )}

      <Card padding="p-0">
        {entries.length === 0 ? <div className="p-8"><Empty title="No transactions yet"/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="ledger-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("date")} className="cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Date {renderSortIndicator("date")}
                  </th>
                  <th onClick={() => handleSort("type")} className="cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Type {renderSortIndicator("type")}
                  </th>
                  <th onClick={() => handleSort("ref")} className="cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Reference {renderSortIndicator("ref")}
                  </th>
                  <th onClick={() => handleSort("desc")} className="cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Description {renderSortIndicator("desc")}
                  </th>
                  <th onClick={() => handleSort("debit")} className="num cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Debit {renderSortIndicator("debit")}
                  </th>
                  <th onClick={() => handleSort("credit")} className="num cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Credit {renderSortIndicator("credit")}
                  </th>
                  <th onClick={() => handleSort("balance")} className="num cursor-pointer select-none group hover:bg-slate-100 transition-colors">
                    Balance {renderSortIndicator("balance")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map(e => (
                  <tr key={e.id}>
                    <td className="font-mono text-xs">{fmtDate(e.transaction_date)}</td>
                    <td><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${e.transaction_type === "Order" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{e.transaction_type}</span></td>
                    <td className="font-mono text-xs">{e.reference_no}</td>
                    <td className="text-slate-600">{e.remarks}</td>
                    <td className="num text-rose-600">{e.debit ? fmtINR(e.debit) : "—"}</td>
                    <td className="num text-emerald-600">{e.credit ? fmtINR(e.credit) : "—"}</td>
                    <td className="num font-semibold">{fmtINR(-e.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
